import React from 'react';
import { Button as MantineButton, Card, Modal, Stack, Text } from '@mantine/core';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { getShortSubscriptionName } from '../../util/helpers';
import { useMutation } from '@apollo/client';
import { gqlTypes } from '../../../../types';
import { useDisclosure } from '@mantine/hooks';
import { activeBillingSubscriptionsDetailsQuery, cancelMembershipMutation } from '../../../../graphql-operations';
import { logInternalError } from '../../../../lib/errors';
import { notifications } from '@mantine/notifications';
import Button from '../../../../components/Button';
import { webSubscriptionManage as events } from '../../../../lib/analytics/events';

interface CancelUnassignedSubscriptionModalProps {
  opened: boolean;
  onClose: () => void;
  subscriptionId: string;
  renewalDate?: DateTime;
}

export function CancelUnassignedSubscriptionModal(props: CancelUnassignedSubscriptionModalProps) {
  const { opened, onClose, subscriptionId, renewalDate } = props;
  const { t } = useTranslation();
  const DATE_FORMAT_TOKEN = 'MMMM d, y';

  const [cancelMutation, cancelMutationState] = useMutation<
    gqlTypes.ECOMMERCE_cancelMembership,
    gqlTypes.ECOMMERCE_cancelMembershipVariables
  >(cancelMembershipMutation, {
    refetchQueries: [{ query: activeBillingSubscriptionsDetailsQuery }],
    onCompleted: (data) => {
      const success = data.cancelMembership?.success ?? false;
      if (!success) {
        notifications.show({
          title: t('failed_to_cancel_membership'),
          message: t('please_try_again_later_or_contact_support'),
          color: 'red',
          autoClose: false,
        });
        logInternalError(`Failed to cancel unassigned membership - ${data.cancelMembership?.reasonForFailure}`);
      } else {
        notifications.show({
          message: t('subscription_successfully_canceled', { count: 1 }),
          color: 'green',
        });

        events.overview.unassignedSubscriptionCancelSuccess({});

        onClose();
      }
    },
    onError: (err) => {
      logInternalError(err);
    },
  });

  const handleCancelMembership = () => {
    events.overview.unassignedSubscriptionCancelConfirmed({});
    cancelMutation({
      variables: {
        input: {
          recurlySubscriptionId: subscriptionId,
        },
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      closeButtonProps={{ iconSize: 24, size: 30, color: 'dark', radius: 'xl' }}
      radius={16}
    >
      <Stack>
        <Text fz={24} fw={600}>
          {t('are_you_sure')}
        </Text>
        <Text fz={16} fw={600}>
          {t('this_will_set_expiration', { date: renewalDate?.toFormat(DATE_FORMAT_TOKEN) })}
        </Text>
        <Button
          onClick={handleCancelMembership}
          loading={cancelMutationState.loading}
          disabled={cancelMutationState.loading}
        >
          {t('confirm_cancellation')}
        </Button>
        <Button tertiary onClick={onClose} disabled={cancelMutationState.loading}>
          {t('nevermind')}
        </Button>
      </Stack>
    </Modal>
  );
}

interface UnassignedSubscriptionCardProps {
  subscriptionId: string;
  renewalMonths?: number;
  renewalDate?: DateTime;
  isCanceled: boolean;
  expirationDate?: DateTime;
}

export default function UnassignedSubscriptionCard({
  renewalMonths,
  renewalDate,
  isCanceled,
  expirationDate,
  subscriptionId,
}: UnassignedSubscriptionCardProps) {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure();
  const DATE_FORMAT_TOKEN = 'MMMM d, y';
  const renewalDateString = renewalDate?.toFormat(DATE_FORMAT_TOKEN) ?? '';
  const expirationDateString = expirationDate?.toFormat(DATE_FORMAT_TOKEN) ?? '';

  let subscriptionDateString;
  if (isCanceled) {
    // TODO: make sure this is actually working
    subscriptionDateString = t('expires_on_date', { date: expirationDateString });
  } else {
    subscriptionDateString = t('renews_on_date', { date: renewalDateString });
  }

  const handleCancelMembership = () => {
    events.overview.unassignedSubscriptionCancelClicked({});
    open();
  };

  const handleCloseCancelMembership = () => {
    events.overview.unassignedSubscriptionCancelClosed({});
    close();
  };

  return (
    <>
      <CancelUnassignedSubscriptionModal
        opened={opened}
        onClose={handleCloseCancelMembership}
        subscriptionId={subscriptionId}
        renewalDate={renewalDate}
      />
      <Card withBorder radius={24}>
        <Stack>
          <Text color="type-light-primary" fw={700} fz={24}>
            {renewalMonths ? getShortSubscriptionName(t, renewalMonths) : t('unknown_subscription_plan')}
          </Text>
          <Text color="type-light-secondary" fw={500} fz={12}>
            {subscriptionDateString}
          </Text>
          {!isCanceled && (
            <MantineButton color="dark" radius="lg" onClick={handleCancelMembership}>
              {t('cancel_membership')}
            </MantineButton>
          )}
        </Stack>
      </Card>
    </>
  );
}
